import React, { useState, useEffect, useMemo } from 'react';
import { collection, getDocs, query, where } from "firebase/firestore";
import { FileText, Clock, CheckCircle } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import Spinner from './Spinner.js';

const leadSourceOptions = [
  'Indiamart',
  'Tradeindia',
  'Website',
  'Existing Customer',
  'Customer Referral',
  'Search Engine (Google, YouTube etc.)',
  'Social Media',
  'Other',
];

const normalizeLeadSource = (value) => {
  if (value === 'Word of Mouth') return 'Customer Referral';
  return value || '';
};

const Quotes = ({ db, user }) => {
  const navigate = useNavigate();
  const [quotes, setQuotes] = useState([]);
  const [loading, setLoading] = useState(true);
  const isAdmin = user?.role === 'admin';

  // --- RESPONSIVE STATE ---
  const [windowWidth, setWindowWidth] = useState(window.innerWidth);
  useEffect(() => {
    const handleResize = () => setWindowWidth(window.innerWidth);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);
  const isMobile = windowWidth <= 768;

  const [itemsPerPage, setItemsPerPage] = useState(() => {
    return Number(sessionStorage.getItem('quotes_itemsPerPage')) || 10;
  });
  const [currentPage, setCurrentPage] = useState(() => {
    return Number(sessionStorage.getItem('quotes_currentPage')) || 1;
  });
  const [sortColumn, setSortColumn] = useState('createdAt');
  const [sortDirection, setSortDirection] = useState('desc');
  const [filters, setFilters] = useState(() => {
    const savedFilters = sessionStorage.getItem('quotes_filters');
    return savedFilters ? JSON.parse(savedFilters) : {
      startDate: '',
      endDate: '',
      salesPerson: '',
      leadSource: '',
      quoteType: '',
      status: '',
      customerName: '',
    };
  });

  // Persist state to sessionStorage
  useEffect(() => {
    sessionStorage.setItem('quotes_filters', JSON.stringify(filters));
    sessionStorage.setItem('quotes_currentPage', currentPage.toString());
    sessionStorage.setItem('quotes_itemsPerPage', itemsPerPage.toString());
  }, [filters, currentPage, itemsPerPage]);

  const handleFilterChange = (e) => {
    const { name, value } = e.target;
    setFilters(prev => ({ ...prev, [name]: value }));
    setCurrentPage(1); // Reset to first page on filter change
  };

  const handleSort = (column) => {
    if (sortColumn === column) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortColumn(column);
      setSortDirection('asc');
    }
    setCurrentPage(1); // Reset to first page on sort change
  };

  const getSortIcon = (column) => {
    if (sortColumn === column) {
      return sortDirection === 'asc' ? ' ▲' : ' ▼';
    }
    return '';
  };

  // Filter and Sorting logic
  const filteredAndSortedQuotes = useMemo(() => {
    let filterableQuotes = [...quotes];

    if (!isAdmin) {
      filterableQuotes = filterableQuotes.filter(quote => quote.ownerUid && quote.ownerUid === user?.uid);
    }

    // Apply filters
    filterableQuotes = filterableQuotes.filter(quote => {
      // Date range filter
      if (filters.startDate || filters.endDate) {
        const quoteDate = new Date(quote.date || quote.createdAt);
        quoteDate.setHours(0, 0, 0, 0);

        if (filters.startDate) {
          const start = new Date(filters.startDate);
          start.setHours(0, 0, 0, 0);
          if (quoteDate < start) return false;
        }

        if (filters.endDate) {
          const end = new Date(filters.endDate);
          end.setHours(0, 0, 0, 0);
          if (quoteDate > end) return false;
        }
      }

      if (filters.salesPerson && quote.salesPerson?.toLowerCase().indexOf(filters.salesPerson.toLowerCase()) === -1) return false;
      if (filters.leadSource && normalizeLeadSource(quote.leadSource).toLowerCase() !== filters.leadSource.toLowerCase()) return false;
      if (filters.quoteType && quote.quoteType?.toLowerCase() !== filters.quoteType.toLowerCase()) return false;
      if (filters.status && quote.status?.toLowerCase() !== filters.status.toLowerCase()) return false;
      if (filters.customerName && quote.customerName?.toLowerCase().indexOf(filters.customerName.toLowerCase()) === -1) return false;
      return true;
    });

    // Apply sorting
    if (sortColumn) {
      filterableQuotes.sort((a, b) => {
        const aValue = a[sortColumn];
        const bValue = b[sortColumn];

        if (sortColumn === 'createdAt') {
            const dateA = new Date(aValue);
            const dateB = new Date(bValue);
            if (sortDirection === 'asc') return dateA - dateB;
            return dateB - dateA;
        } else if (sortColumn === 'grandTotal') {
            if (sortDirection === 'asc') return (aValue || 0) - (bValue || 0);
            return (bValue || 0) - (aValue || 0);
        } else if (typeof aValue === 'string') {
            if (sortDirection === 'asc') return aValue.localeCompare(bValue);
            return bValue.localeCompare(aValue);
        } else {
            if (sortDirection === 'asc') return aValue - bValue;
            return bValue - aValue;
        }
      });
    }
    return filterableQuotes;
  }, [quotes, sortColumn, sortDirection, filters, isAdmin, user?.uid]);

  // Pagination logic
  const totalPages = Math.ceil(filteredAndSortedQuotes.length / itemsPerPage);
  const paginatedQuotes = useMemo(() => {
    const startIndex = (currentPage - 1) * itemsPerPage;
    const endIndex = startIndex + itemsPerPage;
    return filteredAndSortedQuotes.slice(startIndex, endIndex);
  }, [filteredAndSortedQuotes, currentPage, itemsPerPage]);

  useEffect(() => {
    const fetchQuotes = async () => {
      setLoading(true);
      try {
        const quotesRef = collection(db, "quotes");
        const quotesQuery = !isAdmin && user?.uid
          ? query(quotesRef, where('ownerUid', '==', user.uid))
          : quotesRef;

        const querySnapshot = await getDocs(quotesQuery);
        const quotesList = querySnapshot.docs.map(doc => ({ 
          id: doc.id, 
          ...doc.data() 
        }));
        
        // Sort by creation date (newest first)
        // quotesList.sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt));
        
        setQuotes(quotesList);
      } catch (error) {
        console.error("Error fetching quotes: ", error);
      } finally {
        setLoading(false);
      }
    };
    fetchQuotes();
  }, [db, isAdmin, user?.uid]);

  const renderStatus = (status) => {
    switch (status) {
      case 'draft':
        return <span style={{ color: '#f59e0b', display: 'flex', alignItems: 'center', gap: '5px' }}><Clock size={14} /> Draft</span>;
      case 'sent':
        return <span style={{ color: '#10b981', display: 'flex', alignItems: 'center', gap: '5px' }}><CheckCircle size={14} /> Sent</span>;
      case 'invoiced':
        return <span style={{ color: '#2563eb', display: 'flex', alignItems: 'center', gap: '5px' }}><FileText size={14} /> Invoiced</span>;
      default:
        return status;
    }
  };

  const formatDate = (dateString) => {
      const options = { year: 'numeric', month: 'short', day: 'numeric' };
      return new Date(dateString).toLocaleDateString('en-IN', options);
  };
  
  if (loading) return <Spinner />;

  return (
    <div style={{ padding: isMobile ? '10px' : '0' }}>
      <h2 style={{ marginBottom: '20px', fontSize: isMobile ? '20px' : '24px' }}>Quotation List ({filteredAndSortedQuotes.length})</h2>
      
      {/* Filter Section */}
      <div style={{ ...filterSectionStyle, gap: isMobile ? '10px' : '15px', padding: isMobile ? '12px' : '15px' }}>
        <div style={{ ...filterInputGroupStyle, minWidth: isMobile ? '120px' : '150px' }}>
          <label style={filterLabelStyle}>From Date</label>
          <input type="date" name="startDate" value={filters.startDate} onChange={handleFilterChange} style={filterInputStyle} />
        </div>
        <div style={{ ...filterInputGroupStyle, minWidth: isMobile ? '120px' : '150px' }}>
          <label style={filterLabelStyle}>To Date</label>
          <input type="date" name="endDate" value={filters.endDate} onChange={handleFilterChange} style={filterInputStyle} />
        </div>
        <div style={{ ...filterInputGroupStyle, minWidth: isMobile ? '120px' : '150px' }}>
          <label style={filterLabelStyle}>Customer</label>
          <input type="text" name="customerName" value={filters.customerName} onChange={handleFilterChange} placeholder="Search Customer" style={filterInputStyle} />
        </div>
        <div style={{ ...filterInputGroupStyle, minWidth: isMobile ? '120px' : '150px' }}>
          <label style={filterLabelStyle}>Salesperson</label>
          <input type="text" name="salesPerson" value={filters.salesPerson} onChange={handleFilterChange} placeholder="Search Salesperson" style={filterInputStyle} />
        </div>
        <div style={{ ...filterInputGroupStyle, minWidth: isMobile ? '120px' : '180px' }}>
          <label style={filterLabelStyle}>Lead Source</label>
          <select name="leadSource" value={filters.leadSource} onChange={handleFilterChange} style={filterInputStyle}>
            <option value="">All</option>
            {leadSourceOptions.map(option => <option key={option} value={option}>{option}</option>)}
          </select>
        </div>
        <div style={{ ...filterInputGroupStyle, minWidth: isMobile ? '120px' : '150px' }}>
          <label style={filterLabelStyle}>Type</label>
          <select name="quoteType" value={filters.quoteType} onChange={handleFilterChange} style={filterInputStyle}>
            <option value="">All</option>
            <option value="Sales">Sales</option>
            <option value="Rental">Rental</option>
          </select>
        </div>
        <div style={{ ...filterInputGroupStyle, minWidth: isMobile ? '120px' : '150px' }}>
          <label style={filterLabelStyle}>Status</label>
          <select name="status" value={filters.status} onChange={handleFilterChange} style={filterInputStyle}>
            <option value="">All</option>
            <option value="draft">Draft</option>
            <option value="sent">Sent</option>
            <option value="invoiced">Invoiced</option>
          </select>
        </div>
      </div>

      {filteredAndSortedQuotes.length === 0 ? (
        <p style={{ padding: '30px', backgroundColor: '#fff', borderRadius: '8px', border: '1px dashed #d1d5db' }}>
          {isAdmin ? 'No quotations found. Click \'New Quote\' to create your first one.' : 'No quotations found for your account.'}
        </p>
      ) : (
        <div style={{ backgroundColor: 'white', borderRadius: '8px', boxShadow: '0 1px 3px rgba(0,0,0,0.1)', overflowX: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', minWidth: isMobile ? '800px' : 'auto' }}>
            <thead style={{ backgroundColor: '#f9fafb' }}>
              <tr>
                <th style={thStyle} onClick={() => handleSort('createdAt')}>Date{getSortIcon('createdAt')}</th>
                <th style={{...thStyle, textAlign:'left'}} onClick={() => handleSort('customerName')}>Customer{getSortIcon('customerName')}</th>
                <th style={thStyle} onClick={() => handleSort('quoteNumber')}>Quote #{getSortIcon('quoteNumber')}</th>
                <th style={thStyle} onClick={() => handleSort('salesPerson')}>Salesperson{getSortIcon('salesPerson')}</th>
                <th style={thStyle} onClick={() => handleSort('leadSource')}>Lead Source{getSortIcon('leadSource')}</th>
                <th style={thStyle} onClick={() => handleSort('quoteType')}>Type{getSortIcon('quoteType')}</th>
                <th style={thStyle} onClick={() => handleSort('grandTotal')}>Amount{getSortIcon('grandTotal')}</th>
                <th style={thStyle} onClick={() => handleSort('status')}>Status{getSortIcon('status')}</th>
              </tr>
            </thead>
            <tbody>
              {paginatedQuotes.map((quote) => (
                <tr 
                  key={quote.id} 
                  style={{ borderBottom: '1px solid #e5e7eb' }}
                  className="clickable-row"
                  onClick={() => navigate(`/quotes/${quote.id}`)}
                >
                  <td style={tdStyle}>{formatDate(quote.createdAt)}</td>
                 <td style={{...tdStyle, textAlign:'left'}}>
                    <div style={{fontWeight: 'bold', fontSize: '14px'}}>{quote.customerName}</div>
                    <div style={{fontSize: '11px', color: '#6b7280'}}>{quote.subject?.substring(0, 40) || 'No Subject'}...</div>
              </td>
              <td style={tdStyle}>
                <strong style={{fontSize: '13px'}}>{quote.quoteNumber || quote.id.substring(0, 8).toUpperCase()}</strong>
              </td>
                  <td style={tdStyle}>{quote.salesPerson || 'N/A'}</td>
                  <td style={tdStyle}>{normalizeLeadSource(quote.leadSource) || 'N/A'}</td>
                  <td style={tdStyle}>{quote.quoteType}</td>
                  <td style={{...tdStyle, fontWeight: 'bold'}}>
                    ₹{quote.grandTotal ? quote.grandTotal.toLocaleString('en-IN', { maximumFractionDigits: 0 }) : '--'}
                  </td>
                  <td style={tdStyle}>
                    {renderStatus(quote.status)}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>

          {/* Pagination Controls */}
          <div style={{ 
            display: 'flex', 
            flexDirection: isMobile ? 'column' : 'row',
            justifyContent: 'space-between', 
            alignItems: isMobile ? 'flex-start' : 'center', 
            padding: '15px', 
            borderTop: '1px solid #e5e7eb',
            gap: isMobile ? '15px' : '0'
          }}>
            <div style={{ display: 'flex', alignItems: 'center' }}>
              <label style={{ marginRight: '10px', fontSize: '13px', color: '#374151' }}>Items per page:</label>
              <select
                value={itemsPerPage}
                onChange={(e) => {
                  setItemsPerPage(Number(e.target.value));
                  setCurrentPage(1);
                }}
                style={{ padding: '5px 8px', borderRadius: '5px', border: '1px solid #d1d5db', fontSize: '13px' }}
              >
                <option value={10}>10</option>
                <option value={25}>25</option>
                <option value={50}>50</option>
              </select>
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: '10px', width: isMobile ? '100%' : 'auto', justifyContent: isMobile ? 'space-between' : 'flex-end' }}>
              <button
                onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))}
                disabled={currentPage === 1}
                style={paginationButtonStyle}
              >
                Previous
              </button>
              <span style={{ fontSize: '13px', color: '#374151' }}>
                Page {currentPage} of {totalPages}
              </span>
              <button
                onClick={() => setCurrentPage(prev => Math.min(prev + 1, totalPages))}
                disabled={currentPage === totalPages}
                style={paginationButtonStyle}
              >
                Next
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

// --- STYLES ---
const thStyle = { padding: '12px 15px', textAlign: 'center', fontSize: '12px', fontWeight: '600', color: '#374151', textTransform: 'uppercase' };
const tdStyle = { padding: '10px 15px', fontSize: '13px', textAlign: 'center' };
const linkStyle = { color: '#1f2937', textDecoration: 'none', display: 'block' };
const viewButtonStyle = { 
  display: 'inline-flex', alignItems: 'center', gap: '5px', 
  padding: '6px 12px', borderRadius: '4px', 
  backgroundColor: '#e0f2f1', color: '#047857', 
  textDecoration: 'none', fontSize: '12px' 
};

const paginationButtonStyle = { padding: '8px 12px', border: '1px solid #d1d5db', borderRadius: '5px', backgroundColor: '#f9fafb', cursor: 'pointer', fontSize: '13px' };
const filterSectionStyle = { display: 'flex', gap: '15px', marginBottom: '20px', flexWrap: 'wrap', padding: '15px', backgroundColor: '#f9fafb', borderRadius: '8px', border: '1px solid #e5e7eb' };
const filterInputGroupStyle = { display: 'flex', flexDirection: 'column', minWidth: '150px', flex: '1' };
const filterLabelStyle = { fontSize: '11px', fontWeight: '600', color: '#6b7280', marginBottom: '4px', textTransform: 'uppercase' };
const filterInputStyle = { width: '100%', padding: '8px', borderRadius: '5px', border: '1px solid #d1d5db', fontSize: '13px', boxSizing: 'border-box' };

export default Quotes;
